import { CalendarDays, ExternalLink, MapPin, Medal, Trophy, UsersRound } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useContent } from "../context/ContentContext";
import { headerImageStyle } from "../lib/headerImages";
import type { TeamMatchOutcome } from "../types";
import { PublicPageSkeleton } from "../components/LoadingSkeleton";

const outcomeLabels: Record<TeamMatchOutcome, string> = {
  win: "Sieg",
  draw: "Unentschieden",
  loss: "Niederlage",
};

function formatMatchDate(value: string): string {
  if (!value) return "Termin offen";
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }).format(date);
}

export function TeamsPage() {
  const { content, loading } = useContent();
  const { teams } = content;
  const initialSeasonId = teams.seasons.some((season) => season.id === teams.currentSeasonId)
    ? teams.currentSeasonId
    : teams.seasons[0]?.id || "";
  const [selectedSeasonId, setSelectedSeasonId] = useState(initialSeasonId);

  useEffect(() => {
    if (teams.seasons.some((season) => season.id === selectedSeasonId)) return;
    setSelectedSeasonId(initialSeasonId);
  }, [initialSeasonId, selectedSeasonId, teams.seasons]);

  const selectedSeason = useMemo(
    () => teams.seasons.find((season) => season.id === selectedSeasonId) || teams.seasons[0],
    [selectedSeasonId, teams.seasons],
  );

  if (loading) return <PublicPageSkeleton variant="cards" />;

  return (
    <>
      <section className="page-hero page-hero--teams" style={headerImageStyle(content.headers.teams)}>
        <div className="page-width"><span className="eyebrow eyebrow--light"><span /> {teams.eyebrow}</span><h1>{teams.title}</h1><p>{teams.intro}</p></div>
      </section>

      <section className="section page-width teams-page-section">
        {teams.seasons.length > 0 && <div className="season-switcher" aria-label="Saison auswählen">
          <span>Saison</span>
          <div>{teams.seasons.map((season) => <button type="button" key={season.id} className={selectedSeason?.id === season.id ? "active" : ""} aria-pressed={selectedSeason?.id === season.id} onClick={() => setSelectedSeasonId(season.id)}>{season.label}{season.id === teams.currentSeasonId && <small>Aktuell</small>}</button>)}</div>
        </div>}

        {!selectedSeason && <div className="teams-public-empty"><Trophy /><h2>Noch keine Mannschaften eingetragen</h2><p>Sobald die Mannschaftsmeldungen und Ergebnisse gepflegt sind, erscheinen sie an dieser Stelle.</p></div>}

        {selectedSeason && <>
          <div className="teams-season-heading">
            <div><span className="eyebrow"><span /> Saisonübersicht</span><h2>{selectedSeason.label}</h2></div>
            <span><UsersRound /> {selectedSeason.teams.length} {selectedSeason.teams.length === 1 ? "Mannschaft" : "Mannschaften"}</span>
          </div>

          <div className="public-teams-list">
            {selectedSeason.teams.map((team, teamIndex) => {
              const stats = [
                { label: "Spiele", value: team.games },
                { label: "Punkte", value: team.points },
                { label: "Siege", value: team.wins },
                { label: "Unentschieden", value: team.draws },
                { label: "Niederlagen", value: team.losses },
              ].filter((stat) => stat.value.trim());
              const hasLeagueSummary = stats.length > 0 || Boolean(team.tableUrl.trim());
              const hasResults = team.results.length > 0;
              const hasDetails = hasLeagueSummary || hasResults;
              const layoutClassName = `public-team-card-layout${hasLeagueSummary !== hasResults ? ` public-team-card-layout--single public-team-card-layout--${hasLeagueSummary ? "league" : "results"}-only` : ""}`;

              return <article className={`public-team-card${hasDetails ? "" : " public-team-card--identity-only"}`} key={team.id}>
                <header className="public-team-card-head">
                  <span className="public-team-number">{String(teamIndex + 1).padStart(2, "0")}</span>
                  <div>{team.league.trim() && <small>{team.league}</small>}<h2>{team.name}</h2></div>
                  {team.placement.trim() && <span className="team-placement"><Medal /> {team.placement.trim()}</span>}
                </header>

                {hasDetails && <div className={layoutClassName}>
                  {hasLeagueSummary && <aside className="team-league-summary">
                    <span className="team-summary-label"><Trophy /> Liga-Bilanz</span>
                    <div className="team-summary-stats">
                      {stats.map((stat) => <div key={stat.label}><strong>{stat.value}</strong><span>{stat.label}</span></div>)}
                    </div>
                    {team.tableUrl.trim() && <a href={team.tableUrl} target="_blank" rel="noreferrer">Ligatabelle öffnen <ExternalLink /></a>}
                  </aside>}

                  {hasResults && <section className="team-results-panel">
                    <div className="team-results-heading"><div><CalendarDays /><span><strong>Ergebnisse</strong><small>{team.results.length} {team.results.length === 1 ? "Begegnung" : "Begegnungen"}</small></span></div></div>
                    <div className="team-results-list">
                      {team.results.map((result) => <div className={`team-result-row team-result-row--${result.outcome}`} key={result.id}>
                        <time dateTime={result.date}>{formatMatchDate(result.date)}</time>
                        <span className="team-result-opponent"><small><MapPin /> {result.venue === "home" ? "Heimspiel" : "Auswärtsspiel"}</small><strong>{result.opponent}</strong></span>
                        <strong className="team-result-score">{result.score || "–"}</strong>
                        <span className={`team-outcome team-outcome--${result.outcome}`}>{outcomeLabels[result.outcome]}</span>
                      </div>)}
                    </div>
                  </section>}
                </div>}
              </article>
            })}
            {selectedSeason.teams.length === 0 && <div className="teams-public-empty teams-public-empty--compact"><UsersRound /><h2>Keine Mannschaften in dieser Saison</h2><p>Für diese Saison wurden noch keine Mannschaftsdaten hinterlegt.</p></div>}
          </div>
        </>}
      </section>
    </>
  );
}
